import { ColDef, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Location } from '@angular/common';
import { Component, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { AddonStation, BarcodePrintStatusEnum, ItemDetail, PoDetail, PoHeader } from 'app/model/entities/entity-model';
import { JoStatusCode } from 'app/model/enums/jo-status-code';
import { PoStatusCode } from 'app/model/enums/po-status-code';

import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { BarcodeService } from 'app/shared/barcode.service';

import { AgStyleFns } from '@/shared/ag-style-fns';
import { UniAgFns } from '@/shared/uni-ag-fns';
import { DateFns } from 'app/shared/date-fns';
import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { DomainBaseComponent, wrapBusy } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { EditNotesDialogComponent } from 'app/shared/edit-notes-dialog.component';
import { NavFns } from 'app/shared/nav-fns';
import { PoUtilsService } from 'app/shared/po-utils.service';
import _ from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { PurchaseOrderApplyChangeDialogComponent } from './purchase-order-apply-change-dialog.component';

@Component({
  selector: 'app-purchase-order-receive',
  templateUrl: './purchase-order-receive.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PurchaseOrderReceiveComponent extends DomainBaseComponent {
  @ViewChild('applyCell')
  applyCell!: TemplateRef<any>;
  @ViewChild('unapplyCell')
  unapplyCell!: TemplateRef<any>;
  @ViewChild('modifyCell')
  modifyCell!: TemplateRef<any>;
  @ViewChild('noteCell')
  noteCell!: TemplateRef<any>;
  @ViewChild('auditJoCell')
  auditJoCell!: TemplateRef<any>;

  poHeaderId!: number;
  poHeaders!: PoHeader[];
  pohGridOptions!: GridOptionsExt;

  shouldGroupByTransaction = false;

  needsPrintingKey = '_needsPrinting';
  canUnapplyKey = '_canUnapply';
  trxGroupKey = '_trxGroupKey';

  poDetails!: PoDetail[];
  podGridOptions!: GridOptionsExt;
  pohRemainingQty!: number;
  pohIsHeld!: boolean;
  isAccountOwnedPo = false;
  accountOwnedAccountName: string | null = null;
  selectedPod!: PoDetail;

  podsGridOptions!: GridOptionsExt;
  podSummaries: { date: Date; count: number }[] = [];
  shouldShowPodSummaries!: boolean;
  currentDate = new Date();

  tmpTaboo!: string;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected poUtilsService: PoUtilsService,
    protected barcodeService: BarcodeService,
    private location: Location,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.poHeaderId = +this.route.snapshot.params['poHeaderId'];

    this.pohGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPohGridReady,
    });

    this.podGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPodGridReady,
      onRowSelected: this.onPodRowSelected,
      groupDisplayType: 'singleColumn',
      groupRemoveSingleChildren: true,
      // force expanded
      groupDefaultExpanded: -1,
    });
    this.podGridOptions.rowClassRules = {
      'rs-part-of-trx': function (params) {
        // Note: tried all of these to find the right one.
        // return params.data?.poTrxId != null;
        // return params.node.level > 0;
        // return params.node.rowGroupIndex == 0
        // return params.node.expanded;
        // return params.node.childIndex > 0;
        return params.node.uiLevel > 0;
      },
    };

    this.podsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPodsGridReady,
    });

    // needed later....
    await this.dbQueryService.getAll(AddonStation);

    await this.refreshPoDetailsNoBusy();

    this.isPageReady = true;
  }

  private async refreshPoDetailsNoBusy() {
    this.poDetails = await this.dbQueryService.getPoDetailsForReceive(this.poHeaderId);
    this.poDetails.forEach((pod) => {
      const needsPrinting =
        pod.itemDetails != null &&
        pod.itemDetails.length > 0 &&
        pod.itemDetails.some((itd) => itd.barcodePrintStatusId === BarcodePrintStatusEnum.NotYetPrinted);
      (pod as any)[this.needsPrintingKey] = needsPrinting;
      const poTrx = pod.poTrx;
      (pod as any)[this.canUnapplyKey] = poTrx == null || (poTrx && poTrx.podCount == poTrx.poDetails.length);
    });

    if (this.poDetails.length == 0) {
      // This can happen if there are no poDetails for this poHeaderId which can happen if they were unapplied.
      const poHeader = await this.dbQueryService.getPoHeader(this.poHeaderId);
      this.poHeaders = poHeader ? [poHeader] : [];
    } else {
      this.poHeaders = [this.poDetails[0].poHeader];
    }
    this.pohRemainingQty = _.sum(this.poDetails.map((pod) => pod.getRemainingQty()));
    this.pohIsHeld = this.poDetails.some((x) => x.heldQty > 0);
    const acctOwnedPod = this.poDetails.find((pod) => pod.joDetail?.joHeader?.isAccountOwnedOrder);
    this.isAccountOwnedPo = !!acctOwnedPod;
    this.accountOwnedAccountName = acctOwnedPod?.joDetail?.joHeader?.account?.accountName ?? null;
    this.calcPodSummaries(this.selectedPod);

    await this.considerGroupByTrx();
    this.forceUIRefresh();
  }

  @wrapBusy
  private async refetchPoDetails() {
    await this.refreshPoDetailsNoBusy();
  }

  onPohGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: 'Purchase Order', field: 'id' },
      { headerName: 'Manufacturer', field: 'manufacturer.name' },
      { headerName: 'Status', field: 'poStatus.name' },
      { headerName: 'P/O Date', field: 'poDate' },
    ];
    AgFns.initGrid(event, colDefs);
  }

  onPodGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...(UniAgFns.getProductTypeSKUColDef('product.productType.style') as ColDef) },
      { ...(UniAgFns.getProductTypeDescriptionColDef('product.productType.description') as ColDef) },
      { ...(UniAgFns.getFeatureChoicesColDef('product.featureChoicesExtract') as ColDef) },

      {
        headerName: 'Quantities',
        children: [
          {
            headerName: 'Order',
            field: 'orderQty',
            ...AgFns.fixWidth(90),
          },
          {
            headerName: 'Held',
            field: 'heldQty',
            ...AgFns.fixWidth(90),
          },
          {
            headerName: 'Rcvd',
            type: 'numericColumn',
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getReceivedQty();
            },
            ...AgFns.fixWidth(90),
          },
          {
            headerName: 'Dmgd',
            headerTooltip: '(calc) Of those received, how many are damaged, lost or other',
            type: 'numericColumn',
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getDamagedQty();
            },
            ...AgFns.fixWidth(90),
          },
          {
            headerName: 'Cncl',
            type: 'numericColumn',
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getCanceledQty();
            },
            ...AgFns.fixWidth(90),
          },
          {
            headerName: 'Pending',
            type: 'numericColumn',
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getRemainingQty();
            },
            ...AgFns.fixWidth(110),
          },
          {
            headerName: 'Shipped',
            type: 'numericColumn',
            valueGetter: (params) => {
              const pod = <PoDetail>params.data;
              return pod?.getShippedQty();
            },
            ...AgFns.fixWidth(110),
          },
        ],
      },

      {
        headerName: 'Exp. Ship Date',
        field: 'expectedShipDate',
        // valueGetter: (params) => {
        //   const pod = <PoDetail>params.data;
        //   return pod?.joDetail?.getJoDetailShipDate();
        // },
        valueFormatter: (params) => DateFns.fmtDate(params.value),
        cellClass: (params) => {
          const pod = params.data as PoDetail;
          if (pod == null) return '';
          return AgStyleFns.getPodDueDateCellClass(pod.poHeader.isProcessed(), pod.getRemainingQty() > 0, params.value);
        },
        ...AgFns.fixWidth(160),
      },
      {
        ...AgFns.createButtonProps('', this.applyPod.bind(this), {
          colId: 'apply',
          label: 'Apply',
          canDisplay: this.canApplyPod.bind(this),
        }),
        ...AgFns.fixWidth(80),
      },
      {
        ...AgFns.createButtonProps('', this.modifyPod.bind(this), {
          label: 'Modify',
          canDisplay: this.canModifyPod.bind(this),
        }),
        ...AgFns.fixWidth(80),
      },
      {
        ...AgFns.createButtonProps('', this.unapplyPod.bind(this), {
          colId: 'unapply',
          label: 'Unapply',
          canDisplay: this.canUnapplyPod.bind(this),
        }),
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createCheckboxProps('Needs Printing', this.needsPrintingKey),
        suppressHeaderMenuButton: true,
        context: {
          isDisabled: (data: PoDetail) => !(data && data.itemDetails && data.itemDetails.length),
        },
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.editPoNotes.bind(this), {
          label: 'Notes',
          canDisplay: this.canNote.bind(this),
          calcClass: this.calcEditPoNotesClass.bind(this),
        }),
        ...AgFns.fixWidth(80),
      },
      // { headerName: "P/O Detail Id", field: "id", ...AgFns.fixWidth(130) },
      {
        ...AgFns.createButtonProps('', this.goJoAudit.bind(this), {
          label: 'Audit',
          canDisplay: this.canJoAudit.bind(this),
        }),
        ...AgFns.fixWidth(80),
      },
      {
        headerName: 'J/O #',
        ...NavFns.createIdCellClickedNavProps('joDetail.joHeaderId', this.router, '/in-process/manage/job-orders'),
        ...AgFns.fixWidth(90),
      },
      { headerName: 'Account', field: 'joDetail.joHeader.account.accountName', ...AgFns.flexWidth(150) },
      { headerName: 'J/O Status', field: 'joDetail.joHeader.joStatus.name', ...AgFns.fixWidth(200) },
      // DO NOT remove this field.  It is only used when grouping by trx - but it is needed
      {
        headerName: 'Trx Group Key',
        field: this.trxGroupKey,
        rowGroup: this.shouldGroupByTransaction,
        hide: !this.shouldGroupByTransaction,
        ...AgFns.fixWidth(100),
      },
      // For debugging
      // { headerName: "PoTrx Id", field: "poTrxId" },
    ];
    const sortFields = ['product.productType.description', 'product.productType.style', 'product.choiceValue', 'poDetailTypeId'];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: 'asc' as const };
    });

    AgFns.initGrid(event, colDefs, sortModel);
  }




  async onPodRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    const pod = e.data as PoDetail;
    if (!pod) {
      return;
    }
    this.selectedPod = pod;
    this.calcPodSummaries(pod);
  }

  private calcPodSummaries(pod: PoDetail) {
    if (pod == null) return;
    const grps = _.groupBy(pod.itemDetails, (x) => DateFns.startOfDay(x.crtnTs));
    const podSummaries = Object.keys(grps).map((k) => {
      return {
        date: new Date(k),
        count: grps[k].length,
      };
    });
    this.podSummaries = podSummaries;
  }

  onPodsGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: 'Date', field: 'date' },
      { headerName: 'Qty Received', field: 'count' },
    ];
    AgFns.initGrid(event, colDefs, null);
  }

  calcEditPoNotesClass(pod: PoDetail) {
    return this.hasPoNotes(pod) ? 'btn-has-data' : '';
  }

  hasModifiableJo(pod: PoDetail) {
    if (pod.joDetailId == null) {
      return true;
    }
    const joStatusId = pod.joDetail?.joHeader.joStatusId;
    return joStatusId !== JoStatusCode.Closed;
  }

  canApplyAny() {
    if (this.poHeaders?.length > 0) {
      return this.poHeaders[0].poStatusId != PoStatusCode.Complete;
    } else {
      return false;
    }
  }

  canApplyPod(pod: PoDetail) {
    return pod && this.canApplyAny() && this.hasModifiableJo(pod) && (pod.getRemainingQty() > 0 || pod.heldQty > 0);
  }

  @wrapBusy
  async applyPod(pod: PoDetail) {
    if (pod.getRemainingQty() >= this.pohRemainingQty && !this.pohIsHeld) {
      const ok = await this.confirmPoTransition();
      if (!ok) return;
    }
    await this.applyPodCore(pod);
    await this.refetchPoDetails();
  }

  private async applyPodCore(pod: PoDetail) {
    const qty = pod.getRemainingQty();
    await this.poUtilsService.applyPodToDefault(pod, qty);
  }

  async applyAll() {
    const canApplyAll = this.poDetails.some((pod) => {
      return this.hasModifiableJo(pod) && pod.getRemainingQty() > 0;
    });
    if (!canApplyAll) {
      await this.dialogService.showOkMessage('Unable to Apply All', 'There is nothing to apply');
      return false;
    }

    if (!this.pohIsHeld) {
      const ok = await this.confirmPoTransition();
      if (!ok) return;
    }

    await this.wrapBusy(async () => {
      for (const pod of this.poDetails) {
        if (this.canApplyPod(pod)) await this.applyPodCore(pod);
      }

      await this.refetchPoDetails();
    });
  }

  canUnapplyPod(pod: PoDetail) {
    return pod && this.canApplyAny() && this.hasModifiableJo(pod) && (pod.poTrxId != null || pod.getRemainingQty() !== pod.orderQty);
  }

  @wrapBusy
  async unapplyPod(pod: PoDetail) {
    await this.poUtilsService.unapplyPod(pod);
    await this.refetchPoDetails();
  }

  canModifyPod(pod: PoDetail) {
    return pod && this.canApplyAny() && this.hasModifiableJo(pod) && (pod.getRemainingQty() > 0 || pod.heldQty > 0);
  }

  canNote(pod: PoDetail) {
    return pod != null && pod.poDetailTypeId != null;
  }

  async modifyPod(pod: PoDetail) {
    const r = await DialogBaseComponent.open(PurchaseOrderApplyChangeDialogComponent, this, {
      poDetail: pod,
      pohRemainingQty: this.pohRemainingQty,
      pohIsHeld: this.pohIsHeld,
    });
    if (r) {
      await this.refetchPoDetails();
    }
  }

  canPrintBarcodes() {
    return this.poDetails != null && this.poDetails.some((pod) => (pod as any)[this.needsPrintingKey]);
  }

  @wrapBusy
  async printBarcodes() {
    const itemDetails: ItemDetail[] = [];
    this.poDetails.forEach((pod) => {
      if ((pod as any)[this.needsPrintingKey]) {
        const needsPrinting =
          pod.itemDetails != null &&
          pod.itemDetails.length > 0 &&
          pod.itemDetails.some((itd) => itd.barcodePrintStatusId === BarcodePrintStatusEnum.NotYetPrinted);
        if (needsPrinting) {
          const newItemDetails = pod.itemDetails.filter((itd) => itd.barcodePrintStatusId === BarcodePrintStatusEnum.NotYetPrinted);
          itemDetails.push(...newItemDetails);
        } else {
          // TODO: I don't think this fork is ever executed.
          // itemDetails.push(...pod.itemDetails);
        }
      }
    });
    const changes = await this.barcodeService.printItemDetailBarcodesAndNewAddonBinLabels(itemDetails);
    if (changes.length > 0) {
      await this.dbSaveService.saveSelectedChanges(changes);
      await this.refetchPoDetails();
    }
  }

  hasPoNotes(pod: PoDetail) {
    return pod != null && pod.poNotes != null && pod.poNotes.length > 0;
  }

  async editPoNotes(pod: PoDetail) {
    await DialogBaseComponent.open(EditNotesDialogComponent, this, {
      xDetail: pod,
    });
  }

  canJoAudit(pod: PoDetail) {
    return pod?.joDetail != null;
  }

  goJoAudit(pod: PoDetail) {
    const johId = pod?.joDetail && pod.joDetail.joHeaderId;
    if (johId == null) {
      return;
    }
    const params = NavFns.buildNavIdParams(johId);

    this.router.navigate(['/in-process/manage/jo-audit'], params);
  }

  private async confirmPoTransition() {
    return await this.dialogService.yesNoDialog(
      'Are you sure?',
      'This operation will mark this PO complete.  You will not be able to undo any part of this PO after this is completed. Do you want to continue?',
    );
  }

  async considerGroupByTrx() {
    await this.wrapBusy(async () => {
      if (this.shouldGroupByTransaction) {
        await this.getPoTrxHistsForPo();
      }
      this.updateColDefsForGrouping();
    });
  }

  private updateColDefsForGrouping() {
    const gridApi = this.podGridOptions.apiX;
    if (gridApi) {
      const columnDefs = gridApi.getColumnDefs();
      const colDef = <ColDef>columnDefs.find((cd) => (cd as ColDef).field == this.trxGroupKey);

      if (colDef.rowGroup != this.shouldGroupByTransaction) {
        colDef.rowGroup = this.shouldGroupByTransaction;
        gridApi.setGridOption('columnDefs', columnDefs);
      }
    }
  }

  async getPoTrxHistsForPo() {
    const poTrxHists = await this.dbQueryService.getPoTrxHistsForPo(this.poHeaderId);
    const trxMap = new Map<number, number>();
    // Creates a map of PoTrxId -> Group Parent poDetailId
    poTrxHists.forEach((th) => {
      trxMap.set(th.poTrxId, th.poDetailId);
      trxMap.set(th.prevPoTrxId, th.poDetailId);
    });
    this.poDetails.forEach((pod) => {
      let key: number;
      if (pod.poTrxId == null) {
        key = pod.id;
      } else {
        key = trxMap.get(pod.poTrxId) ?? pod.id;
      }
      (pod as any)[this.trxGroupKey] = key;
    });
    this.poDetails = this.poDetails.slice();
    this.podGridOptions.apiX?.refreshClientSideRowModel();
  }

  private forceUIRefresh() {
    // Hack - but this method is called from after each apply/unapply and doesn't repaint properly without this.
    this.pohGridOptions.apiX?.refreshCells({ force: true });
    this.podGridOptions.apiX?.refreshCells({ force: true });
  }

  goAddonBins() {
    this.router.navigate(['/in-process/manage/addon-bins']);
  }
}
